import React from 'react';

import {useSelector} from 'react-redux';

import {RTKGenericAttachedItemsSidebar} from 'app/components/general/GenericAttachedItemsSidebar/RTKGenericAttachedItemsSidebar';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {CurrentUserSelector} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {useAddQuizLink, useRemoveQuizLink} from 'app/quizzes/api';
import {sweetAPI} from 'app/store/sweet-api';
import {runWhenAllRelatedMatch} from 'app/subscriptions';
import {getContentType} from 'app/utils/content-type';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {
  POLICY_RTK_TAGS,
  PROCEDURE_RTK_TAGS,
  PROCESS_RTK_TAGS,
} from 'app/utils/rtk-tags';

import type {GenericActionableType} from 'app/components/general/GenericAttachedItemsSidebar/GenericAttachedItemsSidebarItem';
import type {UseListQueryType} from 'app/store/sweet-api/api-query';
import type {LinkedDocumentType} from 'sweet-types';

type Props<
  ItemType extends GenericActionableType,
  FetchParams extends {page: string},
  UseQueryHookType extends UseListQueryType<FetchParams, ItemType>
> = {
  quizURL: string;
  quizId: string;
  canEdit: boolean;
  contentType: 'procedure' | 'proceduregroup' | 'policy';
  closeUrl: string;
  attachedItems: Array<LinkedDocumentType>;
  createURL: string;
  useDataHook: UseQueryHookType;
  paramsConverter: (query: Record<string, unknown>) => FetchParams;
};

export const QuizGenericObjectsSidebar = <
  ItemType extends GenericActionableType,
  FetchParams extends {page: string},
  UseQueryHookType extends UseListQueryType<FetchParams, ItemType>
>({
  quizId,
  quizURL,
  contentType,
  closeUrl,
  canEdit,
  attachedItems,
  createURL,
  useDataHook,
  paramsConverter,
}: Props<ItemType, FetchParams, UseQueryHookType>) => {
  const {isTeamMember, isManager} = getCurrentUserPerms(
    useSelector((state) => CurrentUserSelector(state)?.data)
  );
  const [addQuizLink] = useAddQuizLink();
  const [removeQuizLink] = useRemoveQuizLink();

  const actions = useAsyncActions({
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  const ct = getContentType(contentType);
  const onRemove = (item: ItemType) => {
    const linkedDocument = attachedItems.find(
      (attachedItem) => attachedItem.url === item.url
    );
    if (linkedDocument != null) {
      return removeQuizLink({url: linkedDocument.link_url}).unwrap();
    }

    throw 'Linked document not found';
  };
  const onAttach = (item: ItemType) => {
    return addQuizLink({
      quiz: quizURL,
      document: item.url,
    }).unwrap();
  };
  return (
    <RTKGenericAttachedItemsSidebar
      attachedItems={attachedItems}
      title={gt('Related %s', ct.pluralName)}
      closeUrl={closeUrl}
      itemComponentProps={(item, attached) => {
        return {
          disableAndShowTooltipText: !canEdit
            ? gt(
                'You have to be a %s manager to add or remove quizzes',
                ct.name
              )
            : undefined,
          actionTooltip: attached
            ? gt('Remove quiz from this %s', ct.name)
            : gt('Add quiz to this %s', ct.name),
        };
      }}
      itemNameProcessor={(object) => getObjectName(object, contentType)}
      handleModelChange={(message) => {
        const ct = contentType === 'proceduregroup' ? 'process' : contentType;
        runWhenAllRelatedMatch(
          message,
          `quiz${ct}`,
          {
            content_type: 'quiz',
            id: parseInt(quizId),
          },
          () => {
            actions.invalidateTags([
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: POLICY_RTK_TAGS.TAGS, id: 'list'},
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: PROCESS_RTK_TAGS.TAGS, id: 'list'},
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
            ]);
          }
        );
      }}
      onRemove={onRemove}
      emptyResultsComponents={
        <NoResultsMessage
          onSidebar={true}
          icon="fa-file"
          head={gt('No %s', ct.pluralName)}
        >
          {isManager
            ? gt(
                'You can attach this quiz to existing %(pluralName)s, detach it from a %(name)s or create a new %(name)s using the button below',
                ct
              )
            : null}
        </NoResultsMessage>
      }
      showFilterField={isManager}
      searchPlaceholder={gt('Search %s...', ct.pluralName)}
      onAttach={onAttach}
      canCreate={isTeamMember}
      createLabel={gt('Create %s', ct.name)}
      createURL={createURL}
      useDataHook={useDataHook}
      paramsConverter={paramsConverter}
    />
  );
};
